import client from 'core/axios';
import handleError from 'core/axios/handleError';
import { handleJSONResponse } from 'core/axios/handleResponse';
import RequestMethod from 'core/axios/RequestMethod';

export const getScheduledCheckinList = ({ params, version = 'v1' } = {}) =>
  client
    .request({
      url: `${version}/checkins/schedules/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const deleteScheduledCheckin = ({ id, params, version = 'v1' } = {}) =>
  client
    .request({
      url: `${version}/checkins/schedules/${id}/`,
      method: RequestMethod.DELETE,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const editScheduledCheckin = ({
  id,
  params,
  data,
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `${version}/checkins/schedules/${id}/`,
      method: RequestMethod.PATCH,
      data,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const addScheduledCheckin = ({ data, version = 'v1' } = {}) =>
  client
    .request({
      url: `${version}/checkins/schedules/`,
      method: RequestMethod.POST,
      data,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getWeightingCounts = ({ params, version = 'v1' }) =>
  client
    .request({
      url: `/${version}/checkins/weighting-counts/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getWeightingStudents = ({ params, version = 'v1' }) =>
  client
    .request({
      url: `/${version}/checkins/weighting-counts/students/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getLatestAnswers = ({ params, version = 'v1' }) =>
  client
    .request({
      url: `/${version}/checkins/latest-answers/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getLatestAnswerStudents = ({ params, version = 'v1' }) =>
  client
    .request({
      url: `/${version}/checkins/latest-answer-students/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getLatestAnswerLessons = ({ params, version = 'v1' }) =>
  client
    .request({
      url: `/${version}/checkins/latest-answer-lessons/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

/**
 * Get Insights
 *
 * @param {Object} params
 * @param {Number} params.limit
 * @param {Number} params.offset
 * @param {Number} params.offset
 * @param {String} params.ordering
 * @param {String} params.action - reported | increased | decreased
 * @param {Number} params.choice - answer id - Only for Reported
 * @param {String} params.category - Overall, Emotional, Social ... (Only for Increased/Decreased)
 * @param {Number} params.count - Only for reported
 * @param {Number} params.duration_weeks - 1 | 2
 * @param {Number} params.compare_duration_weeks - 1 | 2 (Only for reported)
 * @param {Object} params.student scope filters
 */
export const getInsights = ({ params, version = 'v1' }) =>
  client
    .request({
      url: `/${version}/checkins/insights/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getUserCheckins = ({ userId, params, version = 'v1' }) =>
  client
    .request({
      url: `/${version}/checkins/insights/${userId}/checkins/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getInsightsChoices = ({ params, version = 'v1' } = {}) =>
  client
    .request({
      url: `/${version}/checkins/insights/choices/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getCheckinConflicts = ({ data, version = 'v1' } = {}) =>
  client
    .request({
      url: `${version}/checkins/conflicts/`,
      method: RequestMethod.PUT,
      data,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getCheckinConflictedStudentList = ({
  id,
  params,
  version = 'v1',
} = {}) =>
  client
    .request({
      url: `${version}/checkins/conflicts/${id}/students/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getWordsContext = ({ params, version = 'v1' } = {}) =>
  client
    .request({
      url: `/${version}/checkins/words/context/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getWords = ({ params, version = 'v1' } = {}) =>
  client
    .request({
      url: `/${version}/checkins/words/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getWordsDictionary = ({ params, version = 'v1' } = {}) =>
  client
    .request({
      url: `/${version}/checkins/words/global/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const updateWords = ({ data, version = 'v1' } = {}) =>
  client
    .request({
      url: `/${version}/checkins/words/`,
      method: RequestMethod.POST,
      data,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getAlertConfigs = ({ params, version = 'v1' } = {}) =>
  client
    .request({
      url: `/${version}/checkins/emails-configs/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const createAlertConfig = ({ data, version = 'v1' } = {}) =>
  client
    .request({
      url: `${version}/checkins/emails-configs/`,
      method: RequestMethod.POST,
      data,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const updateAlertConfig = ({ configId, data, version = 'v1' } = {}) =>
  client
    .request({
      url: `${version}/checkins/emails-configs/${configId}/`,
      method: RequestMethod.PUT,
      data,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const deleteAlertConfig = ({ configId, version = 'v1' } = {}) =>
  client
    .request({
      url: `${version}/checkins/emails-configs/${configId}/`,
      method: RequestMethod.DELETE,
    })
    .then(handleJSONResponse)
    .catch(handleError);

export const getCheckinsContext = ({ version = 'v1', params = {} } = {}) =>
  client
    .request({
      url: `${version}/checkins/checkins-context/`,
      method: RequestMethod.GET,
      params,
    })
    .then(handleJSONResponse)
    .catch(handleError);
